import { AxisBottom, AxisLeft } from "@visx/axis";
import { curveMonotoneX } from "@visx/curve";
import { Group } from "@visx/group";
import { scaleLinear, scaleTime } from "@visx/scale";
import { LinePath } from "@visx/shape";
import { type FC, useMemo, useRef, useState } from "react";
import type { MetaAdsTrendPoint } from "shared";
import { useResizeObserver } from "usehooks-ts";
import { Card } from "../../components/Card";
import { Select } from "../../components/Select";

interface InsightsTrendChartProps {
	data: MetaAdsTrendPoint[];
}

type MetricKey =
	| "spend"
	| "impressions"
	| "reach"
	| "clicks"
	| "ctr"
	| "cpc"
	| "cpm"
	| "conversions"
	| "costPerResult";

const metricOptions: { key: MetricKey; label: string }[] = [
	{ key: "spend", label: "Spend" },
	{ key: "impressions", label: "Impressions" },
	{ key: "reach", label: "Reach" },
	{ key: "clicks", label: "Clicks" },
	{ key: "ctr", label: "CTR" },
	{ key: "cpc", label: "CPC" },
	{ key: "cpm", label: "CPM" },
	{ key: "conversions", label: "Conversions" },
	{ key: "costPerResult", label: "Cost/Result" },
];

const margin = { top: 20, right: 30, bottom: 50, left: 60 };

export const InsightsTrendChart: FC<InsightsTrendChartProps> = ({ data }) => {
	const [metric, setMetric] = useState<MetricKey>("spend");
	const containerRef = useRef<HTMLDivElement>(
		null,
	) as React.RefObject<HTMLDivElement>;
	const { width = 0, height = 0 } = useResizeObserver({ ref: containerRef });

	const chartData = useMemo(
		() =>
			data.map((d) => ({
				x: new Date(d.date),
				y: d[metric],
			})),
		[data, metric],
	);

	const xScale = useMemo(() => {
		const dates = chartData.map((d) => d.x.getTime());
		return scaleTime({
			domain: [
				new Date(Math.min(...dates, Date.now())),
				new Date(Math.max(...dates, Date.now())),
			],
			range: [margin.left, width - margin.right],
		});
	}, [chartData, width]);

	const yScale = useMemo(() => {
		const values = chartData.map((d) => d.y);
		return scaleLinear<number>({
			domain: [0, Math.max(...values, 1)],
			range: [height - margin.bottom, margin.top],
			nice: true,
		});
	}, [chartData, height]);

	return (
		<Card>
			<Card.Header>
				Trend
				<Select
					className="form-select form-select-sm"
					style={{ width: "auto", marginLeft: "auto" }}
					value={metric}
					onChange={(e) => setMetric(e.target.value as MetricKey)}
				>
					{metricOptions.map((opt) => (
						<option key={opt.key} value={opt.key}>
							{opt.label}
						</option>
					))}
				</Select>
			</Card.Header>
			<Card.Body>
				<div ref={containerRef} style={{ width: "100%", height: "300px" }}>
					{chartData.length === 0 ? (
						<p className="text-muted" style={{ textAlign: "center" }}>
							No trend data available for this date range.
						</p>
					) : (
						<svg width={width} height={height}>
							<title>Insights trend chart</title>
							<Group>
								<LinePath
									data={chartData}
									x={(d) => xScale(d.x)}
									y={(d) => yScale(d.y)}
									curve={curveMonotoneX}
									stroke="var(--bs-primary, #0d6efd)"
									strokeWidth={2}
								/>
								<AxisBottom
									top={height - margin.bottom}
									scale={xScale}
									stroke="#333"
									tickStroke="#333"
									numTicks={Math.min(chartData.length, 10)}
									tickFormat={(d) => {
										const date = d as Date;
										return `${date.getMonth() + 1}/${date.getDate()}`;
									}}
								/>
								<AxisLeft
									left={margin.left}
									scale={yScale}
									stroke="#333"
									tickStroke="#333"
									tickFormat={(d: { valueOf(): number }) =>
										d.valueOf() >= 1000
											? `${(d.valueOf() / 1000).toFixed(1)}k`
											: d.valueOf().toFixed(0)
									}
								/>
							</Group>
						</svg>
					)}
				</div>
			</Card.Body>
		</Card>
	);
};
